import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Megaphone, CheckCircle2, Eye, MousePointerClick, BarChart3, Globe, MapPin, Pencil, Trash2 } from 'lucide-react';
import { useFlashMessage } from '../components/FlashMessage';
import ConfirmDialog from '../components/ConfirmDialog';
import API from '../services/api';

const AdminAdvertisements = () => {
  const navigate = useNavigate();
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adToDelete, setAdToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [adsRes, statsRes] = await Promise.all([
        API.get('/advertisements'),
        API.get('/advertisements/stats/all')
      ]);

      if (adsRes.data.success) setAds(adsRes.data.data);
      if (statsRes.data.success) setStats(statsRes.data.data);
    } catch (error) {
      showDanger('Veriler yüklenirken hata: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ad) => {
    navigate(`/admin/advertisements/edit/${ad.id}`);
  };

  const handleDeleteClick = (ad) => {
    setAdToDelete(ad);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await API.delete(`/advertisements/${adToDelete.id}`);
      showSuccess(`"${adToDelete.title}" başarıyla silindi`);
      setDeleteDialogOpen(false);
      setAdToDelete(null);
      loadData();
    } catch (error) {
      showDanger('Silme başarısız: ' + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7a2c2c]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <FlashContainer />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reklam Yönetimi</h1>
          <p className="text-gray-600 mt-1">Müşteri sayfalarında gösterilecek reklamları yönetin</p>
        </div>
        <div>
          <button
            onClick={() => navigate('/admin/advertisements/new')}
            className="btn-primary flex items-center justify-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Reklam Ekle
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3 lg:gap-4">
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="mb-1 lg:mb-2 flex justify-center"><Megaphone className="w-8 h-8 text-gray-700" /></div>
              <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.total_ads || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Toplam Reklam</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="mb-1 lg:mb-2 flex justify-center"><CheckCircle2 className="w-8 h-8 text-green-600" /></div>
              <p className="text-lg lg:text-2xl font-bold text-green-600">{stats.active_ads || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Aktif Reklam</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="mb-1 lg:mb-2 flex justify-center"><Eye className="w-8 h-8 text-[#7a2c2c]" /></div>
              <p className="text-lg lg:text-2xl font-bold text-[#7a2c2c]">{stats.total_views || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Görüntülenme</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="mb-1 lg:mb-2 flex justify-center"><MousePointerClick className="w-8 h-8 text-purple-600" /></div>
              <p className="text-lg lg:text-2xl font-bold text-purple-600">{stats.total_clicks || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Tıklama</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="mb-1 lg:mb-2 flex justify-center"><BarChart3 className="w-8 h-8 text-orange-600" /></div>
              <p className="text-lg lg:text-2xl font-bold text-orange-600">{stats.avg_ctr || 0}%</p>
              <p className="text-xs lg:text-sm text-gray-600">Ortalama CTR</p>
            </div>
          </div>
        </div>
      )}

      {/* Ads List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Reklamlar</h3>
        </div>
        <div className="card-body p-0">
          {ads.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Görsel
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Başlık
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Gösterim
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Görüntülenme
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tıklama
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CTR
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {ads.map(ad => (
                      <tr key={ad.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <img src={ad.image_url} alt={ad.title} className="h-12 w-20 object-cover rounded border border-gray-200" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{ad.title}</div>
                          {ad.link_url && (
                            <a href={ad.link_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#7a2c2c] hover:underline truncate block max-w-xs">
                              {ad.link_url}
                            </a>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            {ad.display_type === 'all_locations' ? (
                              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 flex items-center gap-1 w-fit">
                                <Globe className="w-3 h-3" /> Tüm Lokasyonlar
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs rounded-full bg-red-50 text-[#7a2c2c] flex items-center gap-1 w-fit">
                                <MapPin className="w-3 h-3" /> {(ad.location_names && typeof ad.location_names === 'string') ? ad.location_names.split(',').length : (ad.location_ids?.length || 0)} Lokasyon
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <span className="font-medium">{ad.view_count || 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <span className="font-medium">{ad.click_count || 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="font-medium">
                            {ad.view_count > 0 ? ((ad.click_count / ad.view_count) * 100).toFixed(2) : 0}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            ad.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {ad.is_active ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleEdit(ad)}
                              className="text-[#7a2c2c] hover:text-[#5a1f1f]"
                              title="Düzenle"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(ad)}
                              className="text-red-600 hover:text-red-800"
                              title="Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-3 p-4">
                {ads.map(ad => (
                  <div key={ad.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    {/* Image and Title */}
                    <div className="flex gap-3 mb-3">
                      <img src={ad.image_url} alt={ad.title} className="h-16 w-24 object-cover rounded border border-gray-200 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 mb-1">{ad.title}</h4>
                        {ad.link_url && (
                          <a href={ad.link_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#7a2c2c] hover:underline truncate block">
                            {ad.link_url}
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Display Type and Status */}
                    <div className="flex items-center gap-2 mb-3">
                      {ad.display_type === 'all_locations' ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 flex items-center gap-1">
                          <Globe className="w-3 h-3" /> Tüm Lokasyonlar
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-red-50 text-[#7a2c2c] flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {(ad.location_names && typeof ad.location_names === 'string') ? ad.location_names.split(',').length : (ad.location_ids?.length || 0)} Lokasyon
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        ad.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {ad.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-3 text-sm">
                      <div className="bg-gray-50 rounded p-2 text-center">
                        <div className="text-xs text-gray-600">Görüntülenme</div>
                        <div className="font-semibold text-gray-900">{ad.view_count || 0}</div>
                      </div>
                      <div className="bg-gray-50 rounded p-2 text-center">
                        <div className="text-xs text-gray-600">Tıklama</div>
                        <div className="font-semibold text-gray-900">{ad.click_count || 0}</div>
                      </div>
                      <div className="bg-gray-50 rounded p-2 text-center">
                        <div className="text-xs text-gray-600">CTR</div>
                        <div className="font-semibold text-gray-900">
                          {ad.view_count > 0 ? ((ad.click_count / ad.view_count) * 100).toFixed(2) : 0}%
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(ad)}
                        className="flex-1 py-2 px-3 bg-red-50 text-[#7a2c2c] rounded-lg text-sm font-medium hover:bg-red-100 flex items-center justify-center gap-1"
                      >
                        <Pencil className="w-4 h-4" /> Düzenle
                      </button>
                      <button
                        onClick={() => handleDeleteClick(ad)}
                        className="flex-1 py-2 px-3 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 flex items-center justify-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" /> Sil
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="mb-4 flex justify-center"><Megaphone className="w-16 h-16 text-gray-400" /></div>
              <p className="text-gray-500 text-lg">Henüz reklam eklenmemiş</p>
              <p className="text-gray-400 text-sm">Yeni reklam eklemek için yukarıdaki butona tıklayın</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Reklam Sil"
        message={`"${adToDelete?.title}" reklamını kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Sil"
        cancelText="İptal"
        type="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default AdminAdvertisements;
